import Vue from 'vue';
import Vuex from 'vuex';
import dotProp from 'dot-prop';
import { api, success, error, findRecursiveInObject } from '@/helpers/utils';
import router from '@/router/router';
import bowser from 'bowser';
/* modules */
import { Snapshot } from './modules/Snapshot';
import { File } from './modules/File';
import { Device } from './modules/Device';
import { Fontfamilies } from './modules/Fontfamilies';
import { Colorschemes } from './modules/Colorschemes';
import { Themes } from './modules/Themes';
import { Iconstyles } from './modules/Iconstyles';
import { Modulecreator } from './modules/Modulecreator';
import { Editor } from './modules/Editor';
import { Settings } from './modules/Settings';
import { Data } from './modules/Data';
import { Listpresets } from './modules/Listpresets';
import { Package } from './modules/Package';
import { Wizard } from './modules/Wizard';
import { Reset } from './modules/Reset';
import { Differ } from './modules/Differ';
import { Textpicker } from './modules/Textpicker';
import { List } from './modules/List';
import { Head } from './modules/Head';
import { Module } from './modules/Module';
import { Page } from './modules/Page';
import { Menu } from './modules/Menu';
import { Configurator } from './modules/Configurator';
import { User } from './modules/User';
import { Route } from './modules/Route';

Vue.use(Vuex);

const store = new Vuex.Store({
    state: {
        session: {
            notSaved: false,
            currentlyPublishing: false
        },
        tecpage: {},
        deha: {}
    },
    modules: {
        Snapshot: Snapshot.module,
        File: File,
        Device: Device,
        Fontfamilies: Fontfamilies,
        Colorschemes: Colorschemes,
        Themes: Themes,
        Iconstyles: Iconstyles,
        Modulecreator: Modulecreator,
        Editor: Editor,
        Settings: Settings,
        Data: Data,
        Listpresets: Listpresets,
        Package: Package,
        Wizard: Wizard,
        Reset: Reset,
        Differ: Differ,
        Textpicker: Textpicker,
        List: List,
        Head: Head,
        Module: Module,
        Page: Page,
        Menu: Menu,
        Configurator: Configurator,
        User: User,
        Route: Route
    },
    getters: {
        /* this is the main getter function */
        /* returns undefined if not set */
        getProp(state) {
            return (source, alternative = null) => {
                let ret = dotProp.get(state, source);
                if (ret === undefined && alternative !== null) {
                    return alternative;
                }
                return ret;
            };
        },
        getSessionNotSaved(state) {
            return state.session.notSaved;
        },
        getSessionCurrentlyPublishing(state) {
            return state.session.currentlyPublishing;
        },
        getObjectNextPos() {
            return (source) => {
                let pos = 1;
                while (Object.values(source).find((el) => el.pos === pos) !== undefined) {
                    pos++;
                }
                return pos;
            };
        },
        storeIsEmpty(state, getters) {
            return getters.getPages === null || Object.keys(getters.getPages).length === 0;
        }
    },
    mutations: {
        set(state, payload) {
            /* due to
            /* https://vuex.vuejs.org/guide/mutations.html#mutations-follow-vue-s-reactivity-rules
            /* https://vuejs.org/v2/guide/list.html#Caveats,
            /* vue loses reactivity when adding new items
            /* we overcome this by using Vue.set */
            let path = payload.source.split('.'),
                prop = path.splice(-1, 1),
                obj = dotProp.get(state, path.join('.'));
            /* recursively traverse back if nested property should be set */
            while (
                (obj === undefined || obj === null || typeof obj !== 'object') &&
                path.length > 1
            ) {
                payload.value = { [prop]: payload.value };
                prop = path.splice(-1, 1);
                obj = dotProp.get(state, path.join('.'));
            }
            if (obj !== undefined && obj !== null && typeof obj === 'object') {
                Vue.set(obj, prop, payload.value);
            }
        },
        remove(state, payload) {
            let path = payload.source.split('.'),
                prop = path.splice(-1, 1),
                obj = dotProp.get(state, path.join('.'));
            if (obj !== undefined && obj !== null && typeof obj === 'object') {
                Vue.delete(obj, prop);
            }
        },
        setSessionNotSaved(state, payload) {
            state.session.notSaved = payload;
        },
        setSessionCurrentlyPublishing(state, payload) {
            state.session.currentlyPublishing = payload;
        },
        setDeha(state, payload) {
            state.deha = payload;
        },
        setTecpage(state, payload) {
            state.tecpage = payload;
        },
        setDehaUser(state, payload) {
            state.deha.user = payload;
        },
        setTecpageUser(state, payload) {
            state.tecpage.user = payload;
        }
    },
    actions: {
        async setProp(context, payload) {
            // set value in store
            context.commit('set', { source: payload.source, value: payload.value });
            // trigger hooks
            await context.dispatch('triggerHooksSetAfter', payload);
            // reset saved state
            await context.commit('setSessionNotSaved', true);
        },
        async addProp(context, payload) {
            let key = 0;
            if (typeof payload.value === 'object' && 'id' in payload.value) {
                key = payload.value.id;
            } else {
                let obj = context.getters.getProp(payload.source);
                if (obj !== undefined) {
                    while (obj[key] !== undefined) {
                        key++;
                    }
                }
            }
            payload.source += '.' + key;
            // add value to store
            context.commit('set', { source: payload.source, value: payload.value });
            // reset saved state
            await context.commit('setSessionNotSaved', true);
        },
        async removeProp(context, payload) {
            // trigger hooks
            await context.dispatch('triggerHooksRemoveBefore', payload);
            // remove value from store
            context.commit('remove', { source: payload.source });
            // reset saved state
            context.commit('setSessionNotSaved', true);
        },
        async triggerHooksSetAfter(context, payload) {
            /* favicon */
            if (payload.source === 'deha.user.misc.favicon') {
                await context.dispatch('updateHeadFavicon');
            }
            if (payload.source === 'deha.user.misc.logo') {
                await context.dispatch('updateHeadFavicon');
            }

            /* logo colors */
            if (payload.source === 'deha.user.misc.logo') {
                await context.dispatch('saveColorsFromCurrentLogo');
            }

            /* fontfamily/colorscheme/iconstyle/theme */
            for (let settings_group of ['fontfamily', 'colorscheme', 'iconstyle', 'theme']) {
                if (payload.source === 'tecpage.user.settings.' + settings_group) {
                    await context.dispatch('updateHtmlClass', settings_group);
                }
            }

            /* theme: appearance of modules */
            if (payload.source === 'tecpage.user.settings.theme') {
                await context.dispatch('setColorAppearanceForMenus');
                await context.dispatch('setColorAppearanceForModules');
            }

            /* team: name */
            if (
                payload.source.indexOf('deha.user.misc.team') === 0 &&
                payload.source.indexOf('data.last_name') ===
                    payload.source.length - 'data.last_name'.length
            ) {
                await context.dispatch('setProp', {
                    source: payload.source.replace('data.last_name', 'data.name'),
                    value: (
                        context.getters.getProp(
                            payload.source.replace('data.last_name', 'data.first_name')
                        ) +
                        ' ' +
                        payload.value
                    ).trim()
                });
            }
            if (
                payload.source.indexOf('deha.user.misc.team') === 0 &&
                payload.source.indexOf('data.first_name') ===
                    payload.source.length - 'data.first_name'.length
            ) {
                await context.dispatch('setProp', {
                    source: payload.source.replace('data.first_name', 'data.name'),
                    value: (
                        payload.value +
                        ' ' +
                        context.getters.getProp(
                            payload.source.replace('data.first_name', 'data.last_name')
                        )
                    ).trim()
                });
            }

            /* gallery: on dynamic height remove layout */
            if (
                payload.source.indexOf('tecpage.user.modules') === 0 &&
                payload.source.indexOf('data.type') ===
                    payload.source.length - 'data.type'.length &&
                payload.value === 'auto' &&
                context.getters.getProp(payload.source.replace('data.type', 'layout.type')) ==
                    '3' &&
                context.getters.getProp(payload.source.replace('data.type', 'type')) === 'gallery'
            ) {
                await context.dispatch('setProp', {
                    source: payload.source.replace('data.type', 'layout.type'),
                    value: 1
                });
            }

            /* certifications: preview in header */
            if (
                payload.source.indexOf('deha.user.misc.certifications') === 0 &&
                payload.value === true
            ) {
                let path = payload.source.split('.');
                context.getters
                    .getListItems('deha.user.misc.certifications', true)
                    .map(function(item) {
                        if (item.id !== path[4]) {
                            context.dispatch('setProp', {
                                source:
                                    'deha.user.misc.certifications.' +
                                    item.id +
                                    '.data.headerPreview',
                                value: false
                            });
                        }
                    });
            }
        },
        async triggerHooksRemoveBefore(context, payload) {
            /* when page is removed */
            if (payload.source.indexOf('tecpage.user.pages.') === 0) {
                let page_id = payload.source.replace('tecpage.user.pages.', '');
                /* redirect to home if needed */
                await context.dispatch('redirectToHomeIfPageDoesNotExist');
                /* remove all modules */
                let modules = context.getters.getProp('tecpage.user.modules');
                if (modules !== undefined) {
                    for (let modules__value of Object.values(modules)) {
                        if (modules__value.page_id === page_id) {
                            await context.dispatch('removeProp', {
                                source: 'tecpage.user.modules.' + modules__value.id
                            });
                        }
                    }
                }
                /* remove all references */
                let references = findRecursiveInObject(context.rootState, 'page_id', page_id);
                if (references.length > 0) {
                    for (let references__value of references) {
                        await context.dispatch('removeProp', { source: references__value });
                    }
                }
            }
        },
        async loadAllData(context) {
            try {
                let response;
                response = await api('deha-api', '/data', 'GET');
                context.commit('setDeha', response.data);
                response = await api('tecpage-api', '/data', 'GET');
                context.commit('setTecpage', response.data);
            } catch (e) {
                error('Es ist ein Fehler aufgetreten!');
                console.error(e);
                return Promise.reject();
            }
        },
        async clearAllData(context) {
            context.commit('setDeha', {});
            context.commit('setTecpage', {});
        },
        async saveAllData(context) {
            try {
                await api('deha-api', '/data/user', 'POST', context.state.deha.user);
                await api('tecpage-api', '/data/user', 'POST', context.state.tecpage.user);
                context.commit('setSessionNotSaved', false);
            } catch (e) {
                error('Es ist ein Fehler aufgetreten!');
                console.error(e);
                return Promise.reject();
            }
        },
        async publishAllDataServerSide(context, tries = 0) {
            if (tries === 0) {
                success('Wird veröffentlicht...');
                context.commit('setSessionCurrentlyPublishing', true);
            }
            if (tries > 3) {
                error('Es ist ein Fehler beim Veröffentlichen aufgetreten');
                context.commit('setSessionCurrentlyPublishing', false);
                return Promise.reject();
            }
            try {
                await context.dispatch('saveAllData');
                await api('tecpage-api', '/publish', 'POST');
                context.commit('setSessionCurrentlyPublishing', false);
            } catch (e) {
                try {
                    await context.dispatch('publishAllDataServerSide', ++tries);
                    context.commit('setSessionCurrentlyPublishing', false);
                } catch (e) {
                    context.commit('setSessionCurrentlyPublishing', false);
                    return Promise.reject();
                }
            }
        },
        async publishAllDataClientSide(context) {
            try {
                await Vue.dialog.confirm(
                    Vue.helpers
                        .uiText('U006')
                        .replace('%DOMAIN%', context.getters.getUserDomainUrl),
                    {
                        cancelText: 'Abbrechen',
                        okText: Vue.helpers.uiText('U007'),
                        animation: 'bounce'
                    }
                );
                success('Wird veröffentlicht...');
                context.commit('setSessionCurrentlyPublishing', true);
                let render = [],
                    pages = context.getters.getPages,
                    browser = bowser.getParser(window.navigator.userAgent).getBrowserName(),
                    copyrights = [];
                for (let pages__value of Object.values(pages)) {
                    await new Promise((resolve) => {
                        let slug = pages__value.slug;
                        slug = slug.replace('/', '');
                        if (slug === '') {
                            slug = null;
                        }
                        router.push(
                            {
                                name: 'render',
                                params: {
                                    page: slug
                                }
                            },
                            () => {
                                resolve();
                            }
                        );
                    });
                    await new Promise((resolve) => {
                        let timeout = setInterval(() => {
                            if (
                                document.querySelector('.loaded-' + pages__value.id) !== null &&
                                document.querySelector('.editor') === null &&
                                document.querySelectorAll('.render').length === 1 &&
                                document.querySelectorAll('img.svginject').length === 0 &&
                                document.querySelectorAll('.dg-container').length === 0
                            ) {
                                window.clearInterval(timeout);
                                resolve();
                            }
                        }, 30);
                    });
                    if (browser === 'Internet Explorer') {
                        await new Promise((resolve) => {
                            setTimeout(() => {
                                resolve();
                            }, 1000);
                        });
                    }
                    let url =
                        window.location.protocol +
                        '//' +
                        window.location.host +
                        '/render' +
                        pages__value.slug;
                    let html = new XMLSerializer().serializeToString(document);
                    html = new DOMParser().parseFromString(html, 'text/html');
                    if (html.querySelector('style') !== null) {
                        html.querySelectorAll('style').forEach((html__value) => {
                            html__value.remove();
                        });
                    }
                    if (html.querySelector('.page-header[style], .page-header [style]') !== null) {
                        html.querySelectorAll('.page-header[style], .page-header [style]').forEach(
                            (html__value) => {
                                html__value.removeAttribute('style');
                            }
                        );
                    }
                    if (html.querySelector('.notifications') !== null) {
                        html.querySelectorAll('.notifications').forEach((html__value) => {
                            html__value.remove();
                        });
                    }
                    if (html.querySelector('.vue-notification-group') !== null) {
                        html.querySelectorAll('.vue-notification-group').forEach((html__value) => {
                            html__value.remove();
                        });
                    }
                    if (html.querySelector('.fonts-preloader + div') !== null) {
                        html.querySelectorAll('.fonts-preloader + div').forEach((html__value) => {
                            html__value.remove();
                        });
                    }
                    if (html.querySelector('.fonts-preloader') !== null) {
                        html.querySelectorAll('.fonts-preloader').forEach((html__value) => {
                            html__value.remove();
                        });
                    }
                    if (html.querySelector('head script') !== null) {
                        html.querySelectorAll('head script').forEach((html__value) => {
                            html__value.remove();
                        });
                    }
                    if (html.querySelector('[data-copyright]') !== null) {
                        html.querySelectorAll('[data-copyright]').forEach((html__value) => {
                            let copyright =
                                '<li>© ' +
                                html__value.getAttribute('data-copyright') +
                                ' (<a href="' +
                                pages__value.slug +
                                '" target="_blank">' +
                                pages__value.name +
                                '</a>)</li>';
                            if (copyrights.indexOf(copyright) === -1) {
                                copyrights.push(copyright);
                            }
                        });
                    }
                    html = new XMLSerializer().serializeToString(html);
                    render.push({
                        url: url,
                        html: html,
                        name: pages__value.name
                    });
                }
                await new Promise((resolve) => {
                    router.push(
                        {
                            name: 'editor'
                        },
                        () => {
                            resolve();
                        }
                    );
                });
                // replace copyrights
                copyrights = '<ul>' + copyrights.join('') + '</ul>';
                render.forEach((render__value) => {
                    if (render__value.name.indexOf('Impressum') > -1) {
                        if (render__value.html.indexOf('%25REPLACE_COPYRIGHTS%25') > -1) {
                            render__value.html = render__value.html.replace(
                                '%25REPLACE_COPYRIGHTS%25',
                                encodeURIComponent(copyrights)
                            );
                        }
                    }
                });
                await context.dispatch('saveAllData');
                await api('tecpage-api', '/publish', 'POST', {
                    render: render
                });
                context.commit('setSessionCurrentlyPublishing', false);
            } catch (e) {
                context.commit('setSessionCurrentlyPublishing', false);
            }
        }
    }
});

export default store;
